import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { isType } from '@sorare/core/src/lib/gql';

import { AnnouncementNotification } from './AnnouncementNotification/index.tsx';
import { AuctionNotification } from './AuctionNotification/index.tsx';
import { AuctionReminderNotification } from './AuctionReminderNotification/index.tsx';
import { AutoClaimNotification } from './AutoClaimNotification/index.tsx';
import { CardCollectionNotification } from './CardCollectionNotification/index.tsx';
import { CardNotification } from './CardNotification/index.tsx';
import { ConversionCreditNotification } from './ConversionCreditNotification/index.tsx';
import { ExternalDepositNotification } from './ExternalDepositNotification/index.tsx';
import { InGameCurrencyNotification } from './InGameCurrencyNotification/index.tsx';
import { KycRequestNotification } from './KycRequestNotification/index.tsx';
import { LoanAvailableNotification } from './LoanAvailableNotification/index.tsx';
import { MarketingNotification } from './MarketingNotification/index.tsx';
import { OfferNotification } from './OfferNotification/index.tsx';
import { ReferralRewardNotification } from './ReferralRewardNotification/index.tsx';
import { SaleNotification } from './SaleNotification/index.tsx';
import { So5LineupNotification } from './So5LineupNotification/index.tsx';
import { TaskNotification } from './TaskNotification/index.tsx';
import { UserRelationNotification } from './UserRelationNotification/index.tsx';
import type { Notification_notification } from './__generated__/index.graphql.ts';
import type { CommonNotificationProps } from './types.ts';

type Props = CommonNotificationProps & {
  notification: Notification_notification;
};

export const Notification = ({ notification, ...rest }: Props) => {
  if (isType(notification, 'AuctionNotification')) {
    return <AuctionNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'SaleNotification')) {
    return <SaleNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'OfferNotification')) {
    return <OfferNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'So5LineupNotification')) {
    return <So5LineupNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'AnnouncementNotification')) {
    return <AnnouncementNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'ReferralRewardNotification')) {
    return <ReferralRewardNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'CardNotification')) {
    return <CardNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'CardCollectionNotification')) {
    return <CardCollectionNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'ExternalDepositNotification')) {
    return (
      <ExternalDepositNotification notification={notification} {...rest} />
    );
  }
  if (isType(notification, 'KycRequestNotification')) {
    return <KycRequestNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'UserRelationNotification')) {
    return <UserRelationNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'ReminderNotification')) {
    return (
      <AuctionReminderNotification notification={notification} {...rest} />
    );
  }
  if (isType(notification, 'MarketingNotification')) {
    return <MarketingNotification notification={notification} />;
  }
  if (isType(notification, 'AutoClaimNotification')) {
    return <AutoClaimNotification notification={notification} />;
  }
  if (isType(notification, 'InGameCurrencyNotification')) {
    return <InGameCurrencyNotification notification={notification} />;
  }
  if (isType(notification, 'LoanNotification')) {
    return <LoanAvailableNotification notification={notification} />;
  }
  if (isType(notification, 'TaskNotification')) {
    return <TaskNotification notification={notification} />;
  }
  if (isType(notification, 'ConversionCreditNotification')) {
    return <ConversionCreditNotification notification={notification} />;
  }
  return null;
};

Notification.fragments = {
  notification: gql`
    fragment Notification_notification on NotificationInterface {
      id
      ...AuctionNotification_auctionNotification
      ...SaleNotification_saleNotification
      ...OfferNotification_offerNotification
      ...So5LineupNotification_so5LineupNotification
      ...AnnouncementNotification_announcementNotification
      ...ReferralRewardNotification_referralRewardNotification
      ...CardNotification_cardNotification
      ...CardCollectionNotification_cardCollectionNotification
      ...ExternalDepositNotification_externalDepositNotification
      ...KycRequestNotification_kycRequestNotification
      ...UserRelationNotification_userRelationNotification
      ...ReminderNotification_auctionReminderNotification
      ...MarketingNotification_marketingNotification
      ...AutoClaimNotification_autoClaimNotification
      ...InGameCurrencyNotification_inGameCurrencyNotification
      ...LoanAvailableNotification_loanNotification
      ...TaskNotification_taskNotification
      ...ConversionCreditNotification_ConversionCreditNotification
    }
    ${AuctionNotification.fragments.auctionNotification}
    ${SaleNotification.fragments.saleNotification}
    ${OfferNotification.fragments.offerNotification}
    ${So5LineupNotification.fragments.so5LineupNotification}
    ${AnnouncementNotification.fragments.announcementNotification}
    ${ReferralRewardNotification.fragments.referralRewardNotification}
    ${CardNotification.fragments.cardNotification}
    ${CardCollectionNotification.fragments.cardCollectionNotification}
    ${ExternalDepositNotification.fragments.externalDepositNotification}
    ${KycRequestNotification.fragments.kycRequestNotification}
    ${UserRelationNotification.fragments.userRelationNotification}
    ${AuctionReminderNotification.fragments.auctionReminderNotification}
    ${MarketingNotification.fragments.marketingNotification}
    ${AutoClaimNotification.fragments.autoClaimNotification}
    ${InGameCurrencyNotification.fragments.inGameCurrencyNotification}
    ${LoanAvailableNotification.fragments.loanNotification}
    ${TaskNotification.fragments.taskNotification}
    ${ConversionCreditNotification.fragments.ConversionCreditNotification}
  ` as TypedDocumentNode<Notification_notification>,
};
